/**
 * PageHeader Component
 * 
 * Standardized page header with title, description, and action buttons.
 * Used across all CRUD pages for consistent look and feel.
 */

import React from 'react';
import { RefreshCw, type LucideIcon } from 'lucide-react';

export interface PageHeaderProps {
  /** Page title */
  title: string;
  /** Page description/subtitle */
  description: string;
  /** Custom action buttons (rendered on the right) */
  actions?: React.ReactNode;
  /** Refresh callback - if provided, shows refresh button */
  onRefresh?: () => void;
  /** Whether refresh is in progress */
  isRefreshing?: boolean;
  /** Primary action button configuration */
  primaryAction?: {
    label: string;
    icon?: LucideIcon;
    onClick: () => void;
    disabled?: boolean;
  };
}

export function PageHeader({
  title,
  description,
  actions,
  onRefresh,
  isRefreshing = false,
  primaryAction,
}: PageHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-gray-600 mt-1">{description}</p>
      </div>
      
      <div className="flex items-center gap-3">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        )}
        
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {primaryAction.icon && <primaryAction.icon className="w-4 h-4" />}
            {primaryAction.label}
          </button>
        )}
        
        {actions}
      </div>
    </div>
  );
}
